import { CloseOutlined } from '@ant-design/icons';
import { Button, Checkbox, Flex, Form, Input, InputRef, Layout, Select, Tooltip, Typography } from 'antd';
import auth, { InviteSignupResponse } from 'api/auth';
import { AxiosError } from 'axios';
import { ISignUpData } from 'interfaces';
import React, { ChangeEvent } from 'react';
import { useDebounce } from 'use-debounce';
import InputMask from 'react-input-mask';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import classes from './SignUp.module.scss';
import TermsModal from './license/TermsModal';
import PasswordRule from './extraRules/PasswordRule';
import { languagesValues } from 'routes/Main/Main';
import PrivacyModal from './license/PrivacyModal';
import CookieModal from './license/CookieModal';
import LogoType from 'assets/img';
import RegistrationTimeline from 'components/RegistrationTimeline/RegistrationTimeline';
import { RuleObject } from 'antd/es/form';
import { getBuildDefaultLanguage } from 'helpers/getBuildDefaultLanguage';
import { useAppSelector } from 'store/hook';
import Spinner from 'components/Spinner/Spinner';
import { editPhoneNumber } from './extraRules/editPhoneNumber';
import VkButton from 'components/LoginButtons/VkButton/VkButton';
import YandexButton from 'components/LoginButtons/YandexButton/YandexButton';
import { functionCollectBrowserData } from 'helpers/browserData/functionCollectBrowserData';

const buildName = window.REACT_APP_BUILD_NAME !== 'REPLACE_REACT_APP_BUILD_NAME' ? window.REACT_APP_BUILD_NAME : process.env.REACT_APP_BUILD_NAME || 'tazadeal';
const authType = window.REACT_APP_AUTH_TYPE !== 'REPLACE_REACT_APP_AUTH_TYPE' ? window.REACT_APP_AUTH_TYPE : process.env.REACT_APP_AUTH_TYPE; 
const loginAdress = authType === 'blitz' ? 'blitz-auth' : 'login';

type SignUpErrorDeatails = {
  message?: string;
  detail?: {
    message: string;
    status: string;
  };
};

export default function SignUp() {
  const [isLaoding, setIsLoading] = React.useState<boolean>(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = React.useState<boolean>(false);
  const [isCookieModalOpen, setIsCookieModalOpen] = React.useState<boolean>(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = React.useState<boolean>(false);
  const [inviteData, setIniteData] = React.useState<InviteSignupResponse | null>(null);
  const [passwordTooltipOpen, setpasswordTooltipOpen] = React.useState(false);
  const [emailForCheck, setEmailForCheck] = React.useState<string>('');
  const [emailChecking, setEmailChecking] = React.useState<boolean>(false);
  const [alreadyCheckedValues, setAlreadyCheckedValues] = React.useState<any[]>([]);
  const [phone, setPhone] = React.useState<string>('');
  const [phoneDeleting, setPhoneDeleting] = React.useState<boolean>(false);
  const [positionPhoneCursor, setPositionPhoneCursor] = React.useState<number>(0);
  const [passwordRules, setPasswordRules] = React.useState<Record<string, 'error' | 'success'>>({
    min: 'error',
    latin: 'error',
    special: 'error',
    number: 'error',
    letter: 'error',
    noSpaces: 'error',
  });
  const { messageApi } = useAppSelector((store) => store.windowState);  

  const navigate = useNavigate();
  const { hash } = useParams();
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const [debouncedeEmailForCheck] = useDebounce(emailForCheck, 1750);
  const { i18n, t } = useTranslation();
  const defaultLanguage = localStorage.getItem('language') || getBuildDefaultLanguage();
  const phoneRef = React.useRef<InputRef | null>(null);

  const ruSegmentQuickAuth = buildName === 'athena' || window.location.origin.includes('test') || window.location.origin.includes('localhost');
  const passwordRuleKeys = Object.keys(passwordRules);
  const arrErrorTexts = ['Invite has expired', 'Email already registered', 'Ошибка регистрации. Соглашение не принято'];
  const arrErrorLocalizationsTexts = [
    t('login.someError'),
    t('login.inviteExpire'),
    t('login.emailAlreadyRegistered'),
    t('login.agreementNotAccepted'),
  ];  

  React.useEffect(() => {
    if (!localStorage.getItem('language')) {
      const initialLanguage = getBuildDefaultLanguage();
      localStorage.setItem('language', initialLanguage);
      i18n.changeLanguage(initialLanguage);
    }
  }, []);

  const checkEmail = async (email: string) => {
    const errors = form.getFieldError('email') || [];
    if (!errors[0]) {
      setEmailChecking(true);
      try {
        const emailExist = await auth.checkUserByEmail({email});
        setAlreadyCheckedValues(prev => [...prev, { email, ...emailExist.data }]);
        if (emailExist.data.success) {
          form.setFields([{ name: 'email', errors: [t('login.emailAlreadyRegistered')] }])
        }
      } catch (e) {
        messageApi?.error(t('login.errorChecked'));
      } finally {
        setEmailChecking(false);
      }
    }
  };

  const checkEmailInAlreadyCheckedValues = () => {
    const errors = form.getFieldError('email') || [];
    if (!errors[0]) {
      const currentEmail = form.getFieldValue('email');
      const isAlreadyChecked = alreadyCheckedValues?.find(answer => answer?.email === currentEmail);
      if (isAlreadyChecked?.message === 'Email already exists' || isAlreadyChecked?.success) {      
        setTimeout(() => {
          form.setFields([{ name: 'email', errors: [t('login.emailAlreadyRegistered')] }]);
        }, 1);
      }
    }
  }

  React.useEffect(() => {
    if (debouncedeEmailForCheck) {
      checkEmail(debouncedeEmailForCheck);
    }
  }, [debouncedeEmailForCheck]);

  React.useEffect(() => {
    if (emailForCheck) {
      checkEmailInAlreadyCheckedValues();   
    }
  }, [emailForCheck]);

  const onLanguageChange = (value: string) => {
    localStorage.setItem('language', value);
    i18n.changeLanguage(value);
    //to translate touched fields when switching
    const allFormFields = form.getFieldsValue(true);
    const namesOfFilledFields = Object.keys(allFormFields);
    form.validateFields(namesOfFilledFields);
    checkEmailInAlreadyCheckedValues();
  };

  React.useEffect(() => {
    const allErrors: any[] = [];
    form.getFieldsError().forEach(item => allErrors.push(item.errors));
    allErrors.flat()[0] && form.validateFields();
  }, [i18n.language]);

  React.useEffect(() => {
    parseHashInvite();
  }, []);

  const checkAccepOfLicenses = async () => {
    try {
      await form.validateFields(['is_accepted_terms', 'is_accepted_cookie', 'is_accepted_privacy']);
      if (inviteData) {
        localStorage.setItem('invite_room_id', inviteData.room_id ?? '');
        localStorage.setItem('invite_group_id', inviteData.group_id ?? '');
        localStorage.setItem('invite_id', inviteData.invite_id ?? '');
        localStorage.setItem('invite_type', inviteData.type ?? '');
      }
      return true;
    } catch {
      return false;
    }
  };

  const parseHashInvite = async () => {
    if (hash) {
      try {
        const response = await auth.inviteSignup(hash);
        if (response.data.is_parent_invite) {
          sessionStorage.setItem('pending_parent_invite', JSON.stringify({
            parent_invite_id: response.data.invite_id,
          }));
          setIniteData(response.data);
          return;
        }

        if (response.data?.is_registered) navigate(`/login/?email=${response.data.email}&prefill=true`);
        form.setFieldsValue({ email: response.data.email });
        setIniteData(response.data);
      } catch {
        messageApi?.error(t('invalidLink'));
        navigate(`/${loginAdress}`)
      }
    }
  };

  const signUpHandle = async (values: ISignUpData) => {
    setIsLoading(true);
    try {
      const place = searchParams.get('place');
      const usersData: ISignUpData = inviteData
        ? {
            ...values,
            invite_type: inviteData.type,
            invite_id: inviteData.invite_id,
            room_id: inviteData.room_id,
            group_id: inviteData.group_id,
          }
        : values;


      const browserData = await functionCollectBrowserData();
      await auth.signup(
        {
          ...usersData,
          email: usersData.email.toLocaleLowerCase(),
          phone_number: usersData.phone_number.replace(/\D/g, ''),
          place,
          details: {
            email: usersData.email.toLocaleLowerCase(),
            phone: usersData.phone_number.replace(/\D/g, ''),
          },
        },
        JSON.stringify(browserData)
      );

      sessionStorage.removeItem('pending_parent_invite');
      navigate(`/confirm-registration?email=${form.getFieldValue('email')}`);
      messageApi?.success({
        content: (
          <span>
            {t('SignUp.success')} - {form.getFieldValue('email')}{' '}
            {/* <CloseCircleOutlined className={classes.closeIcon} onClick={onMessageClose} /> */}
          </span>
        ),
        duration: 5,
      });
    } catch (err) {
      const error = err as AxiosError<SignUpErrorDeatails>;
      const errorText = error.response?.data.message || error.response?.data.detail?.message || '';
      const indexError = arrErrorTexts.findIndex((text) => text === errorText) + 1;
      messageApi?.error(arrErrorLocalizationsTexts[indexError]);
    } finally {
      setIsLoading(false);
    }
  };

  const openLicenseModal = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    setIsTermsModalOpen(true);
  };

  const openCookieModal = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    setIsCookieModalOpen(true);
  };

  const openPrivacyModal = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    setIsPrivacyModalOpen(true);
  };  

  const onPasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const updatedRules: Record<string, 'error' | 'success'> = {
      min: value.length >= 8 ? 'success' : 'error',
      latin: /[a-zA-Z]/.test(value) ? 'success' : 'error',
      special: /[^\w\d\sа-яА-ЯёЁ]/.test(value) ? 'success' : 'error',
      number: /\d/.test(value) ? 'success' : 'error',
      //@ts-ignore
      letter: /\p{L}/u.test(value) ? 'success' : 'error',
      noSpaces: !/\s/.test(value) ? 'success' : 'error',
    };

    setPasswordRules(updatedRules);
  };

  // const onMessageClose: React.MouseEventHandler = (event) => messageApi?.destroy();
  const onPasswordTooltipClose = () => setpasswordTooltipOpen(false);
  const onPasswordTooltipOpen = () => setpasswordTooltipOpen(true);
  const handleFaqClick = () =>  navigate('/ref');

  const extraNameValidator = (_: RuleObject, value: any) => {
    if (value && (value.startsWith(' ') || value.endsWith(' '))) {
      return Promise.reject(new Error(t('PersonalInfo.ban-space-firstandlast')))
    } else if (value && (value.startsWith('-') || value.endsWith('-'))) {
      return Promise.reject(new Error(t('PersonalInfo.ban-firstandlast')))
    } else if (value && /\s{2,}/.test(value)) {
      return Promise.reject(new Error(t('PersonalInfo.onespace')))
    } else if (value && (value.includes(' -') || value.includes('- '))) {
      return Promise.reject(new Error(t('PersonalInfo.ban-spacenear')))
    } else {
      return Promise.resolve();
    }
  };

  const changeForm = (changedValues: any, _: ISignUpData) => {
    for (let key in changedValues) {      
      if (key === 'phone_number') {        
        if (phoneDeleting) {
          const needDeleteAdditionalCharacter = phone.charAt(positionPhoneCursor - 1) === '-' || (phone === changedValues[key]);
          const needDeleteTwoAdditionalCharacter = phone.charAt(positionPhoneCursor - 1) === ' ' || (phone.length < changedValues[key].length);
          if (needDeleteAdditionalCharacter) {
            setPhone(changedValues[key].slice(0, positionPhoneCursor - 2) + changedValues[key].slice(positionPhoneCursor - 1));
            form.setFieldValue(key, changedValues[key].slice(0, positionPhoneCursor - 2) + changedValues[key].slice(positionPhoneCursor - 1));
          } else if (needDeleteTwoAdditionalCharacter) {
            setPhone(changedValues[key].slice(0, positionPhoneCursor - 3) + changedValues[key].slice(positionPhoneCursor - 1));
            form.setFieldValue(key, changedValues[key].slice(0, positionPhoneCursor - 3) + changedValues[key].slice(positionPhoneCursor - 1));
          } else {
            setPhone(changedValues[key]);
          }
          setPhoneDeleting(false);
        } else {          
          if (changedValues[key].length > 18) {
            form.setFieldValue(key, changedValues[key].slice(0, 18));
            setPhone(changedValues[key].slice(0, 18));
          } else {
            setPhone(editPhoneNumber(changedValues[key]));
            form.setFieldValue(key, editPhoneNumber(changedValues[key]))
          }
        }
      }
      if (key === 'email') {
        setEmailForCheck(changedValues[key])
      }
    }
  }

  const onPhoneKeyDown = (e: any) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      const startOfHighlightedText = Number(phoneRef.current?.input?.selectionStart);
      const endOfHighlightedText = Number(phoneRef.current?.input?.selectionEnd);

      (startOfHighlightedText === endOfHighlightedText)
        ? setPositionPhoneCursor(startOfHighlightedText)
        : setPositionPhoneCursor(0);
      if (startOfHighlightedText === endOfHighlightedText) {
        setPhoneDeleting(true);
      } else if (phoneRef.current?.input?.value.length === (endOfHighlightedText - startOfHighlightedText)) {
        form.setFieldValue('phone_number', '+7 (')
        setTimeout(() => {
          phoneRef.current?.input?.setSelectionRange(4, 4);
        }, 1);
      }
    };
  };

  const InputMaskAny = InputMask as any;

  return (
    <div className={classes.mainWrap}>
      <Layout className={classes.signUpElem}>
        <Select
          className={classes.localizationSelect}
          defaultValue={defaultLanguage}
          onChange={onLanguageChange}
          style={{ minWidth: 130 }}
          options={languagesValues}
          optionLabelProp={'label'}
        />
        <div className={classes.signUpCard}>
          <RegistrationTimeline currentStep={0} />
          
          <div className={classes.typography}>
            <img src={LogoType} alt='Logotype' className={classes.logo}/>
            <Typography>
              <Typography.Title level={3}>{t('PersonalInfo.signup')}</Typography.Title>
              <Typography>
                {t('PersonalInfo.haveAcc')}{' '}
                <Typography.Link href={`/${loginAdress}`} className={classes.primary}>
                  {t('PersonalInfo.logIn')}
                </Typography.Link>
              </Typography>
            </Typography>
          </div>

          <Form
            form={form}
            layout='vertical'
            name='signUp'
            className={classes.signUpForm}
            onFinish={signUpHandle}
            onValuesChange={changeForm}
          >
            <Flex gap={30} className={classes.flex}>
              <Form.Item
               className={classes.formItem}
                name='first_name'
                label={t('PersonalInfo.firstname')}
                rules={[
                  {
                    required: true,
                    message: `${t('PersonalInfo.input')}"${t('PersonalInfo.firstname')}"`,
                  },
                  {
                    pattern: new RegExp(/^[a-zA-Zа-яА-ЯёЁ\s-]+$/i),
                    message: t('PersonalInfo.onlyLetters'),
                  },
                  {
                    validator: extraNameValidator
                  },
                ]}
              >
                <Input
                  placeholder={t('PersonalInfo.firstname')}
                  size='large'
                  className={classes.input}
                />
              </Form.Item>

              <Form.Item
                className={classes.formItem}
                name='last_name'
                label={t('PersonalInfo.lastname')}
                rules={[
                  {
                    required: true,
                    message: `${t('PersonalInfo.input')}"${t('PersonalInfo.lastname')}"`,
                  },
                  {
                    pattern: new RegExp(/^[a-zA-Zа-яА-ЯёЁ\s-]+$/i),
                    message: t('PersonalInfo.onlyLetters'),
                  },
                  {
                    validator: extraNameValidator
                  },
                ]}
              >
                <Input
                  placeholder={t('PersonalInfo.lastname')}
                  size='large'
                  className={classes.input}
                />
              </Form.Item>
            </Flex>

            <Flex gap={30} className={classes.flex}>
              <Form.Item
                className={classes.formItem}
                label={t('PersonalInfo.phone')}
                name='phone_number'
                rules={[
                  {
                    required: true,
                    message: `${t('PersonalInfo.input')}"${t('PersonalInfo.phone')}"`,
                  },
                  {
                    pattern: /^\+7\s\(\d{3}\)\s\d{3}-\d{2}-\d{2}$/,
                    message: t('PersonalInfo.incorrectPhone'),
                  },
                ]}
              >
                <InputMaskAny
                  onKeyDown={onPhoneKeyDown}
                  mask={'+7 (999) 999-99-99'}
                  alwaysShowMask={false}
                  maskChar={null}
                >
                  {(inputProps: any) => (
                    <Input
                      className={classes.input}
                      ref={phoneRef}
                      size='large'
                      placeholder={t('PersonalInfo.phone')}
                      {...inputProps}
                    />
                  )}
                </InputMaskAny>
              </Form.Item>

              <Form.Item
                className={classes.formItem}
                label={t('PersonalInfo.email')}
                name='email'
                rules={[
                  {
                    required: true,
                    message: `${t('PersonalInfo.input')}"${t('PersonalInfo.email')}"`,
                  },
                  {
                    type: 'email',
                    message: t('PersonalInfo.emailRule'),
                  },
                ]}
              >
                <Input
                  placeholder={t('PersonalInfo.email')}
                  size='large'
                  disabled={Boolean(hash) && !!inviteData?.email}
                  suffix={emailChecking ? <Spinner size={12} /> : <></>}
                  className={classes.input}
                />
              </Form.Item>
            </Flex>

            <Tooltip
              color='#fff'
              trigger='click'
              classNames={{ root: classes.overlay }}
              open={passwordTooltipOpen}
              title={
                <div className={classes.overlay_container}>
                  <CloseOutlined className={classes.overlay_close} onClick={onPasswordTooltipClose} />
                  {passwordRuleKeys.map((key) => (
                    <PasswordRule key={key} rule={key} type={passwordRules[key]} />
                  ))}
                </div>
              }
            >
              <Form.Item
                name='password'
                label={t('PersonalInfo.password')}
                rules={[
                  {
                    required: true,
                    message: `${t('PersonalInfo.input')}"${t('PersonalInfo.password')}"`,
                  },
                  {
                    pattern: /^(?=.*\d)(?=.*[a-zA-Z])(?=.*\W)(?!.*\s).{8,}$/,
                    message: '',
                  },
                ]}
                dependencies={['confirm']}

              >
                <Input.Password
                  placeholder={t('PersonalInfo.password')}
                  size='large'
                  className={classes.input}
                  onFocus={onPasswordTooltipOpen}
                  onBlur={onPasswordTooltipClose}
                  onChange={onPasswordChange}
                />
              </Form.Item>
            </Tooltip>

            <Form.Item
              name='confirm'
              label={t('PersonalInfo.confirm')}
              rules={[
                { required: true, message: `${t('PersonalInfo.confirm')}` },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('PersonalInfo.confirmText')));
                  },
                }),
              ]}
              dependencies={['password']}
            >
              <Input.Password
                placeholder={t('PersonalInfo.confirm')}
                size='large'
                className={classes.input}
              />
            </Form.Item>

            <Form.Item
              name='is_accepted_terms'
              valuePropName='checked'
              // отменено в рамках задачи №18998
              // getValueFromEvent={(e) => {
              //   if (e.target.checked) openLicenseModal(e);
              // }}
              rules={[
                {
                  validator: (_, value) =>
                    value ? Promise.resolve() : Promise.reject(new Error(t('PersonalInfo.acceptedAgreementError'))),
                },
              ]}
            >
              <Checkbox className={classes.checkboxStyle}>
                <span className={classes.licenseButton}>
                  {t('PersonalInfo.acceptAgreement')}{' '}
                  <span className={classes.link} onClick={(e) => openLicenseModal(e)}>
                    {t('PersonalInfo.terms')}
                  </span>{' '}
                  {t('PersonalInfo.system')}
                </span>
              </Checkbox>
            </Form.Item>

            <Form.Item
              name='is_accepted_cookie'
              valuePropName='checked'
              style={{ marginTop: -10 }}
              // отменено в рамках задачи №18998
              // getValueFromEvent={(e) => {
              //   if (e.target.checked) openCookieModal(e);
              // }}
              rules={[
                {
                  validator: (_, value) =>
                    value ? Promise.resolve() : Promise.reject(new Error(t('PersonalInfo.acceptCookieError'))),
                },
              ]}
            >
              <Checkbox className={classes.checkboxStyle}>
                <span className={classes.licenseButton}>
                  {t('PersonalInfo.acceptAgreement')}{' '}
                  <span className={classes.link} onClick={(e) => openCookieModal(e)}>
                    {t('PersonalInfo.cookie')}
                  </span>{' '}
                </span>
              </Checkbox>
            </Form.Item>

            <Form.Item
              name='is_accepted_privacy'
              valuePropName='checked'
              style={{ marginTop: -10 }}
              // отменено в рамках задачи №18998
              // getValueFromEvent={(e) => {
              //   if (e.target.checked) openPrivacyModal(e);
              // }}
              rules={[
                {
                  validator: (_, value) =>
                    value ? Promise.resolve() : Promise.reject(new Error(t('PersonalInfo.acceptPolicyError'))),
                },
              ]}
            >
              <Checkbox className={classes.checkboxStyle}>
                <span className={classes.licenseButton}>
                  {t('PersonalInfo.acceptAgreement')}{' '}
                  <span className={classes.link} onClick={(e) => openPrivacyModal(e)}>
                    {t('PersonalInfo.privacy')}
                  </span>{' '}
                </span>
              </Checkbox>
            </Form.Item>

            <Form.Item shouldUpdate style={{marginBottom: 16}}>
              {() => (
                <Button
                  type='primary'
                  htmlType='submit'
                  loading={isLaoding}
                  // disabled={!form.isFieldsTouched(true) || !!form.getFieldsError().filter(({ errors }) => errors.length).length}
                >
                  {t('PersonalInfo.sign')}
                </Button>
              )}
            </Form.Item>
          </Form>


          {ruSegmentQuickAuth &&
            <>
              <div className={classes.orGroupBtns}>{t('login.external.btns.orRegiter')}</div>
              <Flex gap="middle" className={classes.socialBtns}>
                <YandexButton preLogic={checkAccepOfLicenses} action={'register'}/>
                <VkButton preLogic={checkAccepOfLicenses} action={'register'}/>
              </Flex>
            </>
          }
        </div>

        <Button
          type='primary'
          className={classes.faqButton}
          onClick={handleFaqClick}
        >
          FAQ
        </Button>
      </Layout>

      {isTermsModalOpen && (
        <React.Suspense fallback={<div />}>
          <TermsModal
            isTermsModalOpen={isTermsModalOpen}
            setIsTermsModalOpen={setIsTermsModalOpen}
            t={t}
            form={form}
          />
        </React.Suspense>
      )}

      {isCookieModalOpen && (
        <React.Suspense fallback={<div />}>
          <CookieModal
            isCookieModalOpen={isCookieModalOpen}
            setIsCookieModalOpen={setIsCookieModalOpen}
            t={t}
            form={form}
          />
        </React.Suspense>
      )}

      {isPrivacyModalOpen && (
        <React.Suspense fallback={<div />}>
          <PrivacyModal
            isPrivacyModalOpen={isPrivacyModalOpen}
            setIsPrivacyModalOpen={setIsPrivacyModalOpen}
            t={t}
            form={form}
          />
        </React.Suspense>
      )}
    </div>
  );
}
